// Central date formatter — every user-facing date/time in the app renders
// through here so we have ONE place to change the format.
//
// Iter 295 (2026-02-16) — format rule locked to MM/DD/YY for full dates
// and MM/YY for month-only labels. Two invariants app-wide:
//   1. Full date on every user-facing surface → MM/DD/YY  (e.g. 09/16/26)
//   2. Month-only label (charts, KPI subtitles) → MM/YY   (e.g. 09/26)
//   3. `at` timestamps NEVER roll to next UTC day — day-key via
//      `ctDateKey(iso)` asks Intl for the America/Chicago calendar day.

const CT_TZ = 'America/Chicago';
const pad = (n) => String(n).padStart(2, '0');

// Intl formatter that gives us the Central calendar date parts of any
// wall-clock instant. Cached because DateTimeFormat construction is
// non-trivial and this runs in every table row.
const _ctPartsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: CT_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
});
const _ctPartsFmtTime = new Intl.DateTimeFormat('en-US', {
  timeZone: CT_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: 'numeric', minute: '2-digit', hour12: true,
});

function _ctParts(v) {
  if (v == null || v === '') return null;
  let d;
  if (v instanceof Date) d = v;
  else {
    const s = String(v);
    // MM-DD-YYYY or MM/DD/YYYY — construct a UTC noon date so any tz
    // shift can't roll to an adjacent day.
    const mdy = s.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})$/);
    if (mdy) {
      d = new Date(Date.UTC(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]), 12, 0, 0));
    } else {
      // Date-only ISO 'YYYY-MM-DD' → UTC noon (same reason).
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (iso) {
        d = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12, 0, 0));
      } else {
        d = new Date(s);
      }
    }
  }
  if (!d || isNaN(d)) return null;
  const parts = _ctPartsFmt.formatToParts(d);
  const out = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = p.value;
  return out;
}

/** MM/DD/YY (iter 295 lock). Returns '—' for null/empty. */
export function fmtDate(v, fallback = '—') {
  const p = _ctParts(v);
  if (!p) return fallback;
  return `${p.month}/${p.day}/${String(p.year).slice(-2)}`;
}

/** MM/DD/YY hh:mm AM/PM (America/Chicago). */
export function fmtDateTime(v, fallback = '—') {
  if (v == null || v === '') return fallback;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return fallback;
  const parts = _ctPartsFmtTime.formatToParts(d);
  const g = Object.fromEntries(parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
  return `${g.month}/${g.day}/${String(g.year).slice(-2)} ${g.hour}:${g.minute} ${g.dayPeriod || ''}`.trim();
}

/** MM/DD/YY compact — alias for fmtDate. */
export function fmtDateShort(v, fallback = '—') {
  return fmtDate(v, fallback);
}

/** MM/YY (iter 295 lock). For chart axis + KPI subtitles. Accepts
 *  either a `YYYY-MM` label, a `YYYY-MM-DD` date, or a Date instance.
 *  2026-02-21 hardening: if the input is a non-empty short string that
 *  fails every parse path (e.g. bare 3-letter month names shipped by
 *  the SimplePractice Income Received Report — 'Jan', 'Feb', …), we
 *  now return the raw string rather than a dash. Prior behaviour hid
 *  every X-axis label on the BI Revenue Momentum chart. */
export function fmtMonYY(v) {
  if (v == null || v === '') return '—';
  // Bare YYYY-MM (chart labels) — convert directly, don't round-trip.
  if (typeof v === 'string') {
    const ym = v.match(/^(\d{4})-(\d{2})$/);
    if (ym) return `${ym[2]}/${ym[1].slice(-2)}`;
  }
  const p = _ctParts(v);
  if (!p) {
    // Fall back to the raw string if it's already a human-readable
    // month label (e.g. 'Jan', 'Feb', 'JAN 2026'). Anything else → em-dash.
    if (typeof v === 'string' && v.trim().length && v.trim().length <= 10) return v;
    return '—';
  }
  return `${p.month}/${String(p.year).slice(-2)}`;
}

/** Full month + full year (e.g. 'July, 2026'). Kept for callers that
 *  still want the verbose form; most surfaces should use fmtMonYY. */
export function fmtMonthYear(v, fallback = '—') {
  const p = _ctParts(v);
  if (!p) return fallback;
  const monthName = new Intl.DateTimeFormat('en-US', {
    timeZone: CT_TZ, month: 'long',
  }).format(new Date(Date.UTC(Number(p.year), Number(p.month) - 1, 15, 12)));
  return `${monthName}, ${p.year}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Iter 255 — TZ-aware day bucketing. Use these when grouping / filtering
// events by day. Never `iso.slice(0, 10)` again — that's the UTC day.
// ─────────────────────────────────────────────────────────────────────────

/**
 * `YYYY-MM-DD` day key in America/Chicago for any timestamp. Feeds
 * chart x-axis buckets, `byDay` maps, "same-day" comparisons, etc.
 */
export function ctDateKey(v) {
  if (v == null || v === '') return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return '';
  const p = _ctPartsFmt.formatToParts(d);
  const g = Object.fromEntries(p.filter(x => x.type !== 'literal').map(x => [x.type, x.value]));
  return `${g.year}-${g.month}-${g.day}`;
}

/** Today's Central date as `YYYY-MM-DD`. Replacement for
 *  `new Date().toISOString().slice(0, 10)` which returns UTC. */
export function todayCTKey() {
  return ctDateKey(new Date());
}

/**
 * Weekday index (1=Mon .. 7=Sun) of the Central-time day for the given
 * timestamp. Replaces `new Date(day + 'T00:00:00Z').getUTCDay()`
 * patterns that always ran in UTC.
 */
export function ctWeekdayIso(v) {
  const key = ctDateKey(v);
  if (!key) return null;
  // Reconstruct at UTC noon — same day key regardless of timezone.
  const [y, m, d] = key.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
  return dow === 0 ? 7 : dow;
}
